import { useUpdateApiToken } from '@/queries/api-tokens';
import { getErrorMessage } from '@/utils';
import Alert from 'antd/es/alert';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import DatePicker from 'antd/es/date-picker';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Modal from 'antd/es/modal';
import Switch from 'antd/es/switch';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useTheme } from 'styled-components';

import type {
  ApiTokenResponse,
  UpdateApiTokenRequest,
} from '@mai/platform-api/src/routes/api-tokens.schema';

interface EditTokenModalProps {
  open: boolean;
  onCancel: () => void;
  token: ApiTokenResponse | null;
}

interface FormValues {
  name: string;
  isActive: boolean;
  expiresAt?: dayjs.Dayjs | null;
}

export const EditTokenModal: React.FC<EditTokenModalProps> = ({
  open,
  onCancel,
  token,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const updateTokenMutation = useUpdateApiToken();
  const theme = useTheme();

  useEffect(() => {
    if (token && open) {
      form.setFieldsValue({
        name: token.name,
        isActive: token.isActive,
        expiresAt: token.expiresAt ? dayjs(token.expiresAt) : null,
      });
    }
  }, [token, open, form]);

  useEffect(() => {
    if (open && token) {
      form.resetFields();
    }
  }, [open, form, token]);

  const handleSubmit = async () => {
    if (!token) return;

    try {
      const values = await form.validateFields();
      const requestData: UpdateApiTokenRequest = {
        name: values.name,
        isActive: values.isActive,
        expiresAt: values.expiresAt ? values.expiresAt.toDate() : null,
      };

      await updateTokenMutation.mutateAsync({
        tokenId: token.tokenId,
        data: requestData,
      });

      void message.success('API token updated successfully');
      onCancel();
    } catch (error) {
      void message.error(getErrorMessage(error, 'Failed to update API token'));
    }
  };

  const isDeactivating =
    token?.isActive && form.getFieldValue('isActive') === false;

  return (
    <Modal
      title="Edit API Token"
      open={open}
      onCancel={onCancel}
      width={500}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSubmit}
          loading={updateTokenMutation.isPending}
        >
          Save Changes
        </Button>,
      ]}
    >
      {token && (
        <Form form={form} layout="vertical">
          <Alert
            type="info"
            message="Token Information"
            description={
              <div>
                <div>
                  <strong>Token ID:</strong>{' '}
                  <span style={{ fontFamily: 'monospace' }}>
                    {token.tokenId}
                  </span>
                </div>
                <div>
                  <strong>Role:</strong> {token.role}
                </div>
                <div>
                  <strong>Created:</strong>{' '}
                  {new Date(token.createdAt).toLocaleString()}
                </div>
                {token.lastUsedAt && (
                  <div>
                    <strong>Last Used:</strong>{' '}
                    {new Date(token.lastUsedAt).toLocaleString()}
                  </div>
                )}
              </div>
            }
            showIcon
            style={{ marginBottom: `${theme.margin}px` }}
          />

          <Form.Item
            name="name"
            label="Token Name"
            rules={[
              { required: true, message: 'Please enter a token name' },
              { min: 1, max: 255, message: 'Name must be 1-255 characters' },
            ]}
          >
            <Input placeholder="e.g., Production API Access, CI/CD Pipeline" />
          </Form.Item>

          <Form.Item name="isActive" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          {isDeactivating && (
            <Alert
              type="warning"
              message="Deactivating Token"
              description="Deactivating this token will immediately invalidate all active sessions using it."
              showIcon
              style={{ marginBottom: `${theme.margin}px` }}
            />
          )}

          <Form.Item
            name="expiresAt"
            label="Expiration"
            help="Leave empty for no expiration"
          >
            <DatePicker
              showTime
              placeholder="Select expiration date"
              disabledDate={(current) => current && current < dayjs()}
              style={{ width: '100%' }}
              allowClear
            />
          </Form.Item>

          <Alert
            type="warning"
            message="Note"
            description="The token role cannot be changed after creation. To change the role, create a new token and delete this one."
            showIcon
          />
        </Form>
      )}
    </Modal>
  );
};
